import { useEffect, useState } from "react";
import axios from "axios";
import { X, Truck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";

/**
 * Driver picker modal. Used in two flows:
 *   1) `mode="assign"` — logística advances embarque → en_ruta. Confirm picks a driver
 *      and calls onConfirm(driver) which should call /advance with assigned_driver_id.
 *   2) `mode="reassign"` — logística/master changes the assigned driver on an en_ruta
 *      delivery. Confirm calls onConfirm(driver) which should call /reassign.
 */
export default function DriverPickerModal({ delivery, mode = "assign", onConfirm, onClose }) {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/drivers`);
        if (active) setDrivers(res.data || []);
      } catch (err) {
        console.error("[DriverPicker] load drivers failed:", err);
        toast.error("No se pudo cargar la lista de choferes");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const handleSubmit = async () => {
    if (!selected) {
      toast.error("Elige un chofer / entregas locales");
      return;
    }
    setSubmitting(true);
    try {
      await onConfirm(selected);
    } finally {
      setSubmitting(false);
    }
  };

  const current = delivery?.assigned_driver_id;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start sm:items-center justify-center bg-zinc-950/60 backdrop-blur-sm p-4"
      data-testid="driver-picker-modal"
    >
      <div className="bg-white border border-zinc-200 w-full max-w-md my-4 sm:my-8 flex flex-col max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-4rem)]">
        <div className="flex items-center justify-between p-5 border-b border-zinc-200 flex-shrink-0 bg-white">
          <div className="min-w-0 flex-1 pr-3">
            <p className="eyebrow text-zinc-500">
              {mode === "reassign" ? "Reasignar entrega" : "Asignar a chofer"}
            </p>
            <h2 className="font-display text-xl font-bold mt-1 truncate">{delivery?.cliente}</h2>
            <p className="text-sm text-zinc-500 mt-0.5 truncate">{delivery?.herramienta}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-100 flex-shrink-0" data-testid="btn-close-driver-picker">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1 min-h-0">
          {loading ? (
            <div className="py-8 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
          ) : drivers.length === 0 ? (
            <p className="text-sm text-zinc-500 text-center py-8">
              No hay choferes ni entregas locales activos. Pídele al admin que cree uno.
            </p>
          ) : (
            <ul className="space-y-2">
              {drivers.map((d) => {
                const isCurrent = current === d.user_id;
                const isSel = selected?.user_id === d.user_id;
                return (
                  <li key={d.user_id}>
                    <button
                      type="button"
                      onClick={() => setSelected(d)}
                      disabled={isCurrent}
                      data-testid={`driver-option-${d.user_id}`}
                      className={`w-full text-left p-3 border-2 transition-colors flex items-center gap-3 ${
                        isCurrent
                          ? "border-zinc-200 bg-zinc-50 opacity-50 cursor-not-allowed"
                          : isSel
                            ? "border-ekin-600 bg-ekin-50"
                            : "border-zinc-200 hover:border-zinc-400"
                      }`}
                    >
                      <Truck className={`h-5 w-5 flex-shrink-0 ${isSel ? "text-ekin-700" : "text-zinc-400"}`} />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm truncate">{d.name}</p>
                        <p className="text-xs text-zinc-500 truncate">
                          {d.role === "entregas_locales" ? "Entregas locales" : "Chofer"} · {d.email}
                        </p>
                      </div>
                      {isCurrent && (
                        <span className="eyebrow text-zinc-500 text-[10px] flex-shrink-0">Actual</span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="p-5 border-t border-zinc-200 flex items-center justify-end gap-2 flex-shrink-0 bg-white">
          <button
            onClick={onClose}
            type="button"
            className="btn-tactile px-4 py-2 border border-zinc-300 text-sm hover:bg-zinc-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            type="button"
            disabled={!selected || submitting}
            data-testid="btn-confirm-driver"
            className="btn-tactile px-5 py-2 bg-ekin-600 text-white text-sm font-medium hover:bg-ekin-700 disabled:opacity-40 inline-flex items-center gap-2"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {mode === "reassign" ? "Reasignar" : "Asignar y avanzar"}
          </button>
        </div>
      </div>
    </div>
  );
}
